import { Injectable } from '@angular/core';
import {
  ISummaryData,
  ProductCatalogResponse,
  ServiceConfiguration,
} from '../../../../core/models/summary-data';
import {
  BookRequest,
  ProductCatalogParams,
} from '../../../../core/interfaces/funnel.interface';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { catchError, delay, Observable } from 'rxjs';
import { PaymentSessionResponse } from '../../../../core/models/payment-session-model';
import { IVehicleData } from '../../../../core/models/vehicle-data.model';
import { SummaryService } from '../../../../core/services/summary.service';

@Injectable({
  providedIn: 'root',
})
export class FunnelService {
  private productsUrl = environment.products_url;
  private identityUrl = environment.identity_url;
  private europeIdentity = environment.europe_identity;

  summaryData: ISummaryData | null = null;

  constructor(private http: HttpClient, private router: Router, private summaryService: SummaryService) {}

  ngOnInit() {
    this.summaryData = this.summaryService.getSummary();
  }

  getProductCatalog(params: ProductCatalogParams) {
    return this.http
      .get<ProductCatalogResponse>(`${this.productsUrl}/product-catalog`, {
        params: { ...params },
      })
      .pipe(
        delay(2000),
        catchError((error) => {
          console.error('Error getting product catalog:', error);
          throw error;
        })
      );
  }

  bookService(dto: BookRequest, productType: string): Observable<any> {
    const endpoint = `${this.productsUrl}/consumer/products/${productType}`;
    return this.http.post(endpoint, dto);
  }

  prepareDtoForRequest(summaryData: ISummaryData): BookRequest {
    const serviceConfiguration = summaryData.serviceConfiguration
      ? this.handleServiceConfiguration(summaryData.serviceConfiguration)
      : {};
    const dto = {
      ZoneId: summaryData.productInformation?.ZoneId ?? '',
      FinishZoneId: summaryData.productInformation?.FinishZoneId ?? '',
      ProductId: summaryData.productInformation?.product?.Id ?? '',
      ServiceStartDate: summaryData.locationInfo?.ServiceStartDate ?? '',
      ServiceEndDate: summaryData.locationInfo?.ServiceEndDate ?? '',
      Comments: summaryData.locationInfo?.comments ?? '',
      OriginContactName: summaryData.locationInfo?.originContactLastName ?? '',
      OriginContactPhoneNumber:
        summaryData.locationInfo?.originContactPhoneNumber ?? '',
      OriginContactEmailAddress:
        summaryData.locationInfo?.originContactEmailAddress ?? null,
      IsOriginContactExternal: true,
      OriginAddress: summaryData.locationInfo?.originAddress ?? '',
      OriginAddressDetails:
        summaryData.locationInfo?.originAddressDetails ?? '',
      OriginAddressLatitude:
        summaryData.locationInfo?.originAddressLatitude ?? 0,
      OriginAddressLongitude:
        summaryData.locationInfo?.originAddressLongitude ?? 0,
      DestinationContactName:
        summaryData.locationInfo?.destinationContactName ?? '',
      DestinationContactPhoneNumber:
        summaryData.locationInfo?.destinationContactPhoneNumber ?? '',
      DestinationContactEmailAddress:
        summaryData.locationInfo?.destinationContactEmailAddress ?? null,
      IsDestinationContactExternal: true,
      DestinationAddress: summaryData.locationInfo?.destinationAddress ?? '',
      DestinationAddressDetails:
        summaryData.locationInfo?.destinationAddressDetails ?? '',
      DestinationAddressLatitude:
        summaryData.locationInfo?.destinationAddressLatitude ?? 0,
      DestinationAddressLongitude:
        summaryData.locationInfo?.destinationAddressLongitude ?? 0,
      VehicleChassisType: summaryData.vehicleData?.vehicleType ?? 0, // Assign default value of 0 if undefined
      VehicleFuelType: summaryData.vehicleData?.fuelType ?? 0, // Assign default value of 0 if undefined
      LicensePlate: summaryData.vehicleData?.licensePlate ?? '',
      VehicleBrand: summaryData.vehicleData?.brand ?? '',
      VehicleModel: summaryData.vehicleData?.model ?? '',
      ServiceConfiguration: serviceConfiguration ?? {},
      RequiresPreTechnicalInspection: false, // ⚠️ No aparece en summaryData
      RequiresTechnicalInspection: false, // ⚠️ No aparece en summaryData
    };

    console.log('dto', dto);
    return dto;
  }

  handleServiceConfiguration(serviceConfiguration: ServiceConfiguration): any {
    if (this.summaryData?.productInformation?.product?.id === 'refueling') {
      return {
        FuelType: serviceConfiguration.fuelType ?? 0,
        MonetaryAmount: serviceConfiguration.monetaryAmount ?? 0,
      };
    }
    return serviceConfiguration;
  }

  addTopping(
    productId: string,
    orderHash: string,
    serviceConfiguration: ServiceConfiguration,
    toppingType: string
  ) {
    let body: any;

    switch (toppingType) {
      case 'refill':
        body = {
          ProductId: productId,
          ServiceConfiguration: JSON.stringify({
            MonetaryAmount: serviceConfiguration.monetaryAmount,
            FuelType: serviceConfiguration.fuelType,
          }),
        };
        break;

      case 'insurance':
      case 'accessory':
      case 'replacement-vehicle':
        body = {
          ProductId: productId,
          ServiceConfiguration: JSON.stringify(serviceConfiguration),
        };
        break;

      case 'wash':
      case 'topping-vehicle-data-collection':
      case 'tyres':
        body = {
          ProductId: productId,
        };
        break;

      default:
        console.error('Tipo de topping no reconocido:', toppingType);
        return;
    }

    return this.http.patch(
      `${this.productsUrl}/consumer/order/${orderHash}/topping`,
      body
    );
  }

  removeTopping(orderHash: string, productId?: string, removeAll?: boolean) {
    const params: any = {};

    if (removeAll) {
      params.removeAll = 'true';
    } else if (productId) {
      params.productId = productId;
    }

    return this.http
      .delete(`${this.productsUrl}/consumer/order/${orderHash}/topping`, {
        params,
      })
      .pipe(
        catchError((error) => {
          console.error('Error removing topping:', error);
          throw error;
        })
      );
  }

  finalBook(orderHash: string): Observable<any> {
    return this.http
      .post(
        `${this.productsUrl}/consumer/order`,
        { orderHash: orderHash, paymentMethod: 2 },
        {
          observe: 'response',
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error finalizing booking:', error);
          throw error;
        })
      );
  }

  createPaymentSession(orderHash: string): Observable<any> {
    return this.http.post<any>(
      `${this.productsUrl}/consumer/order/${orderHash}/kyc/payment-session`,
      {}
    );
  }

  confirmPaymentSession(orderHash: string, body: any): Observable<void> {
    return this.http.put<void>(
      `${this.productsUrl}/consumer/order/${orderHash}/kyc/payment-session`,
      body
    );
  }

  getVehicleData(licensePlate: string) {
    return this.http
      .get<IVehicleData>(`${this.productsUrl}/vehicle`, {
        params: { licensePlate },
      })
      .pipe(
        catchError((error) => {
          console.error('Error getting vehicle data:', error);
          throw error;
        })
      );
  }

  getBrandingStyles(brandingId: string): Observable<any> {
    return this.http
      .get<any>(`${this.europeIdentity}/branding-details?brandingId=${brandingId}`, {})
      .pipe(
        catchError((error) => {
          console.error('Error getting branding details data:', error);
          throw error;
        })
      );
  }

  getValetStations(productId: string) {
    return this.http
      .get(`${this.productsUrl}/info/stations`, {
        params: { productId },
      })
      .pipe(
        catchError((error) => {
          console.error('Error getting valet stations:', error);
          throw error;
        })
      );
  }

  checkDateTime(selectedDate: Date, currentDate: Date = new Date()): boolean {
    currentDate.setHours(0, 0, 0);
    const warningEnd = new Date();
    warningEnd.setDate(currentDate.getDate() + 1);
    warningEnd.setHours(23, 59, 59);

    const dayOfWeekToday = currentDate.getDay();
    const dayOfWeekSelected = selectedDate.getDay();

    const isTodayFriday = dayOfWeekToday === 5;
    const isSelectedDateMonday = dayOfWeekSelected === 1;

    return (
      (selectedDate >= currentDate && selectedDate <= warningEnd) ||
      (isTodayFriday && isSelectedDateMonday)
    );
  }
}
